import * as T from "three";
import { DecalGeometry } from "three/addons/geometries/DecalGeometry.js";
import { Component } from "./live-codec.js";

function textures(source, s3tc) {
    return (source.frameFaces ?? [source.faces]).map(faces => {
        const mipmaps = faces[0], first = mipmaps[0];
        const texture = source.compressed && s3tc
            ? new T.CompressedTexture(mipmaps, first.width, first.height,
                [13, 20].includes(source.format) ? T.RGBA_S3TC_DXT1_Format : source.format === 14 ? T.RGBA_S3TC_DXT3_Format : T.RGBA_S3TC_DXT5_Format)
            : new T.DataTexture(first.data, first.width, first.height, T.RGBAFormat,
                first.data instanceof Float32Array ? T.FloatType : T.UnsignedByteType);
        if (!source.compressed) texture.mipmaps = mipmaps;
        texture.colorSpace = T.SRGBColorSpace; texture.wrapS = texture.wrapT = T.ClampToEdgeWrapping;
        texture.minFilter = mipmaps.length > 1 ? T.LinearMipmapLinearFilter : T.LinearFilter;
        texture.magFilter = T.LinearFilter; texture.generateMipmaps = false; texture.needsUpdate = true;
        return texture;
    });
}

export function sprayOrientation(normal) {
    const up = Math.abs(normal.z) > .999 ? new T.Vector3(0, 1, 0) : new T.Vector3(0, 0, 1);
    const right = new T.Vector3().crossVectors(up, normal).normalize();
    up.crossVectors(normal, right).normalize();
    return new T.Euler().setFromRotationMatrix(new T.Matrix4().makeBasis(right, up, normal));
}

export class LiveSprays {
    constructor(view, rpc) { this.view = view; this.rpc = rpc; this.entries = new Map(); this.closed = false; }
    diagnostics() {
        const result = { received: this.entries.size, loading: 0, projected: 0, waiting: 0, failed: 0 };
        for (const entry of this.entries.values()) result[entry.failed ? 'failed' : !entry.loaded ? 'loading' : entry.projected ? 'projected' : 'waiting']++;
        return result;
    }
    reconcile(entities) {
        this.entities = entities;
        if (this.view.debugOptions?.renderSprays === false) { this.needsReconcile = true; return; }
        this.needsReconcile = false;
        const wanted = new Set();
        for (const [key, entity] of entities) {
            const state = entity.state.components[Component.Spray];
            if (!state?.crc || !state.serial) continue;
            wanted.add(key);
            let entry = this.entries.get(key);
            const signature = `${state.crc}:${state.serial}`;
            if (entry?.signature !== signature) {
                if (entry) this.remove(key, entry);
                entry = { signature, state, resources: new Set(), object: new T.Group(), loaded: false };
                this.entries.set(key, entry);
                void this.load(key, entry);
            }
            entry.state = state;
        }
        for (const [key, entry] of this.entries) if (!wanted.has(key)) this.remove(key, entry);
    }
    async load(key, entry) {
        try {
            const source = await this.rpc("spray", { crc: entry.state.crc, s3tc: !!this.view.s3tc });
            if (this.closed || this.entries.get(key) !== entry) return;
            entry.frames = textures(source.texture, this.view.s3tc);
            for (const texture of entry.frames) entry.resources.add(texture);
            entry.fps = source.fps;
            entry.material = new T.MeshBasicMaterial({ map: entry.frames[0], transparent: true, depthWrite: false,
                polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4, side: T.FrontSide, toneMapped: false });
            entry.resources.add(entry.material); entry.loaded = true;
        } catch (error) {
            entry.failed = true;
            if (!this.closed && this.entries.get(key) === entry && error.name !== "AbortError")
                this.view.diagnostic?.(`Player spray unavailable: ${error.message}`);
        }
    }
    project(entry, target) {
        const position = new T.Vector3(...entry.state.position), normal = new T.Vector3(...entry.state.normal).normalize();
        if (target) {
            target.object.updateWorldMatrix(true, true);
            position.applyMatrix4(target.object.matrixWorld); normal.transformDirection(target.object.matrixWorld);
        }
        const orientation = sprayOrientation(normal);
        const texture = entry.frames[0], image = texture.image;
        const width = image?.width ?? 256, height = image?.height ?? 256;
        const size = new T.Vector3(width >= height ? 64 : 64 * width / height, height >= width ? 64 : 64 * height / width, 8);
        const candidates = [];
        const targetBasis = target?.object.matrixWorld;
        if (target) { target.object.updateWorldMatrix(true, true); target.object.traverse(mesh => { if (mesh.isMesh && !mesh.isInstancedMesh) candidates.push(mesh); }); }
        else for (const item of this.view.meshes ?? []) if (item.model === 0) candidates.push(item.mesh);
        let projected = false;
        for (const mesh of candidates) {
            if (!mesh.geometry?.attributes.position) continue;
            mesh.updateWorldMatrix(true, false);
            mesh.geometry.computeBoundingBox();
            const box = mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrixWorld);
            if (!box.expandByScalar(64).containsPoint(position)) continue;
            const geometry = new DecalGeometry(mesh, position, orientation, size);
            if (!geometry.attributes.position.count) { geometry.dispose(); continue; }
            // A decal only belongs on surfaces facing out of its projection plane.
            const positions = geometry.attributes.position.array, normals = geometry.attributes.normal.array;
            const uv = geometry.attributes.uv.array, keep = { position: [], normal: [], uv: [] };
            for (let i = 0; i < positions.length; i += 9) {
                if (normals[i] * normal.x + normals[i + 1] * normal.y + normals[i + 2] * normal.z <= .05) continue;
                keep.position.push(...positions.slice(i, i + 9)); keep.normal.push(...normals.slice(i, i + 9));
                keep.uv.push(...uv.slice(i / 3 * 2, i / 3 * 2 + 6));
            }
            geometry.setAttribute("position", new T.Float32BufferAttribute(keep.position, 3));
            geometry.setAttribute("normal", new T.Float32BufferAttribute(keep.normal, 3));
            // Source VTF row zero is image top, unlike DecalGeometry's bottom UV.
            geometry.setAttribute("uv", new T.Float32BufferAttribute(keep.uv.map((value, i) => i % 2 ? 1 - value : value), 2));
            if (!keep.position.length) { geometry.dispose(); continue; }
            if (target) geometry.applyMatrix4(targetBasis.clone().invert());
            entry.resources.add(geometry); entry.object.add(new T.Mesh(geometry, entry.material)); projected = true;
        }
        if (!projected) return false;
        (target?.object ?? this.view.scene).add(entry.object);
        entry.target = target; entry.initialPosition = position; entry.projected = true;
        return true;
    }
    update(time) {
        if (this.needsReconcile && this.view.debugOptions?.renderSprays !== false) this.reconcile(this.entities);
        const localTime = performance.now();
        for (const entry of this.entries.values()) {
            entry.object.visible = this.view.debugOptions?.renderSprays !== false;
            if (!entry.object.visible) continue;
            if (!entry.loaded) continue;
            const target = entry.state.hitIndex ? this.entities?.get(`${entry.state.hitIndex}:${entry.state.hitGeneration}`) : null;
            if (entry.projected && entry.target === target && entry.object.parent !== (target?.object ?? this.view.scene)) {
                // LiveScene clears children when an asynchronously loaded model
                // replaces a placeholder or appearance changes. Recover the
                // decal using the new surface, keeping textures/material alive.
                entry.object.clear();
                for (const resource of entry.resources) if (resource.isBufferGeometry) { resource.dispose(); entry.resources.delete(resource); }
                entry.projected = false; entry.retryAt = 0;
            }
            if (!entry.projected) {
                if (entry.state.hitIndex && (!target || target.pending)) {
                    this.waiting(entry, 'waiting for the struck entity or its model'); continue;
                }
                if (localTime < (entry.retryAt ?? 0)) continue;
                if (!this.project(entry, target)) { entry.retryAt = localTime + 250; this.waiting(entry, 'no matching surface at the received position'); }
            }
            if (entry.projected && entry.target && entry.target !== target) { entry.object.visible = false; continue; }
            if (entry.projected && !entry.target) entry.object.position.fromArray(entry.state.position).sub(entry.initialPosition);
            const frame = Math.floor(Math.max(0, time - entry.state.startTime) / 1000 * entry.fps) % entry.frames.length;
            entry.material.map = entry.frames[frame];
        }
    }
    waiting(entry, reason) {
        entry.waitingSince ??= performance.now();
        if (!entry.warned && performance.now() - entry.waitingSince >= 2000) {
            entry.warned = true;
            this.view.diagnostic?.(`Player spray not projected: ${reason}.`);
        }
    }
    remove(key, entry) { entry.object.removeFromParent(); for (const resource of entry.resources) resource.dispose(); this.entries.delete(key); }
    clear() { for (const [key, entry] of this.entries) this.remove(key, entry); this.needsReconcile = false; this.entities = null; }
    dispose() { this.closed = true; this.clear(); }
}
